import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';

/**
 * A wide, centered modal (06-designsystem.md) for editing surfaces that need
 * room to spread across two columns — larger and calmer than the right-hand
 * {@link Drawer}. On a phone it becomes a near-full-screen sheet so the same
 * flows stay completable. Escape closes, the backdrop closes, the body scroll is
 * locked while open, focus moves into the panel, and the open/close animation is
 * dropped for `prefers-reduced-motion`.
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    // Move focus into the dialog so Escape and tabbing start from inside it.
    const toFocus = panelRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    toFocus?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-center sm:items-center sm:p-4">
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.div
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === 'string' ? title : 'Detaljer'}
            className="border-hairline bg-panel/95 backdrop-blur-glass shadow-panel relative z-10 flex h-full w-full flex-col border outline-none sm:h-auto sm:max-h-[92vh] sm:w-[min(1120px,95vw)] sm:rounded-panel"
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.98, y: 10 }}
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.98, y: 10 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          >
            <header className="border-hairline flex items-start gap-3 border-b px-5 py-4 sm:px-6">
              <div className="min-w-0 flex-1">
                <div className="font-display text-ink text-base leading-snug">{title}</div>
                {subtitle && <div className="mt-1.5">{subtitle}</div>}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Lukk"
                className="text-ink-soft hover:bg-raised hover:text-ink -mr-1 -mt-1 rounded-lg p-2 transition"
              >
                <X size={18} aria-hidden />
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">{children}</div>

            {footer && (
              <footer className="border-hairline bg-base/40 border-t px-5 py-3 sm:px-6">
                {footer}
              </footer>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
